import React, { useState, useMemo } from 'react';
import { FiXCircle, FiUserPlus, FiHash, FiTag } from 'react-icons/fi';
import QuantityModal from './QuantityModal';
import LineItemAdjustModal from './LineItemAdjustModal';

const Cart = ({
  cart,
  salePreview,
  loyaltyDiscount,
  loyaltyPoints = 0,
  selectedPromotions = [],
  onRemovePromotion,
  onUpdateQuantity,
  onAdjustItem,
  onRemoveItem,
  onClearCart,
  onProcessPayment,
  onCreateB2BOrder,
  onAddCustomer,
  onRemoveCustomer,
  onRedeemPoints,
  onClearLoyaltyRedemption,
  selectedCustomer,
  hasUnpricedItems,
  canCreateB2BOrder = false,
  isCreatingB2BOrder = false,
  discountReasons = [],
  canApplyDiscount = false,
}) => {
  const [activeQuantityItem, setActiveQuantityItem] = useState(null);
  const [activeAdjustItem, setActiveAdjustItem] = useState(null);
  const itemCount = useMemo(
    () => cart.reduce((sum, item) => sum + (item.quantity || 0), 0),
    [cart]
  );
  const subtotal = salePreview ? salePreview.subtotal : cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const promoDiscount = salePreview ? salePreview.totalDiscount : 0;
  const totalAfterPromos = subtotal - promoDiscount;
  const finalTotal = totalAfterPromos - (loyaltyDiscount || 0);
  const displayCartItems = [...cart].reverse();
  const previewItemsByKey = useMemo(() => {
    if (!salePreview?.items || salePreview.items.length !== cart.length) return new Map();
    const pairs = cart.map((item, idx) => [item.lineId || item.productId, salePreview.items[idx]]);
    return new Map(pairs);
  }, [salePreview, cart]);

  const getManualDiscount = (item) => {
    if (!item) return 0;
    const baseUnitPrice = Number.isFinite(item.basePrice) ? item.basePrice : item.price || 0;
    const lineTotal = baseUnitPrice * (item.quantity || 0);
    if (item.discountType === 'percent') {
      const percent = Number(item.discountValue || 0);
      if (!Number.isFinite(percent) || percent <= 0) return 0;
      return Math.min(lineTotal, (lineTotal * percent) / 100);
    }
    if (item.discountType === 'amount') {
      const amount = Number(item.discountValue || 0);
      if (!Number.isFinite(amount) || amount <= 0) return 0;
      const perItemDiscount = Math.min(baseUnitPrice, amount);
      return Math.min(lineTotal, perItemDiscount * (item.quantity || 0));
    }
    return 0;
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg p-4 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b pb-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Current sale</h2>
          <p className="text-xs text-slate-500">{itemCount} item{itemCount === 1 ? '' : 's'} in cart</p>
        </div>
        {selectedCustomer ? (
          <div className="text-right">
            <p className="text-sm font-semibold text-slate-900">{selectedCustomer.fullName}</p>
            <p className="text-xs text-slate-500">{selectedCustomer.customerId}</p>
            <button
              type="button"
              onClick={onRemoveCustomer}
              className="text-xs text-slate-400 hover:text-rose-500 mt-1"
            >
              Remove
            </button>
            {selectedCustomer.loyaltyCard && onRedeemPoints && (
              <button
                type="button"
                onClick={onRedeemPoints}
                className="text-xs text-primary flex items-center gap-1 mt-1"
              >
                <FiUserPlus className="text-[12px]" /> Redeem
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onAddCustomer}
            className="flex items-center gap-1 text-sm font-semibold text-primary"
          >
            <FiUserPlus /> Attach customer
          </button>
        )}
      </div>
      
      <div className="mt-4 border rounded-2xl p-4 bg-slate-50">
        {selectedPromotions && selectedPromotions.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {selectedPromotions.map((promo) => (
              <span
                key={promo.promoId}
                className="px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold flex items-center gap-2"
              >
                {promo.name}
                {onRemovePromotion && (
                  <button
                    type="button"
                    className="text-primary hover:text-primary/70"
                    onClick={() => onRemovePromotion(promo.promoId)}
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-gray-700">
            <span>Total items</span>
            <span>{itemCount}</span>
          </div>
          {promoDiscount > 0 && (
            <div className="flex justify-between text-accent-green">
              <span>Promotional Discounts</span>
              <span>-${promoDiscount.toFixed(2)}</span>
            </div>
          )}
          {loyaltyDiscount > 0 && (
            <div className="flex justify-between items-center text-blue-600 font-semibold">
              <span>
                Loyalty points redeemed
                {loyaltyPoints > 0 ? ` (${loyaltyPoints} pts)` : ''}
              </span>
              <div className="flex items-center gap-2">
                <span>-${loyaltyDiscount.toFixed(2)}</span>
                {onClearLoyaltyRedemption && (
                  <button
                    type="button"
                    onClick={onClearLoyaltyRedemption}
                    className="text-xs text-slate-500 font-semibold hover:text-rose-500"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between font-bold text-2xl my-4 pt-2 border-t border-slate-200">
          <span>Total</span>
          <span>${finalTotal.toFixed(2)}</span>
        </div>
        <div className="flex space-x-2">
          <button onClick={onClearCart} className="w-1/3 bg-gray-300 text-gray-800 py-3 rounded-md font-semibold hover:bg-gray-400">Clear</button>
          <button
            onClick={onProcessPayment}
            disabled={cart.length === 0 || hasUnpricedItems}
            className="w-2/3 bg-primary text-white py-3 rounded-md font-semibold hover:bg-primary-dark disabled:bg-gray-400 z-10"
          >
            {hasUnpricedItems ? 'Enter prices' : 'Pay'}
          </button>
        </div>
        {canCreateB2BOrder && (
          <button
            type="button"
            onClick={onCreateB2BOrder}
            disabled={cart.length === 0 || hasUnpricedItems || isCreatingB2BOrder}
            className="mt-2 w-full bg-slate-900 text-white py-3 rounded-md font-semibold hover:bg-slate-800 disabled:bg-slate-400"
          >
            {isCreatingB2BOrder ? 'Creating B2B order...' : 'Create B2B Order'}
          </button>
        )}
      </div>

      {cart.length === 0 ? (
        <div className="flex-grow flex items-center justify-center text-gray-500">Cart is empty</div>
      ) : (
        <div className="flex-grow overflow-y-auto mt-4 pr-1 min-h-0">
          <div className="flex flex-col gap-2">
          {displayCartItems.map(item => {
            const itemKey = item.lineId || item.productId;
            const previewItem =
              previewItemsByKey.get(itemKey) ||
              salePreview?.items.find(p => p.product === item.productId);
            const manualDiscount = getManualDiscount(item);
            const promoDiscount =
              previewItem?.discount && previewItem.discount > manualDiscount
                ? previewItem.discount - manualDiscount
                : 0;
            const baseUnitPrice = Number.isFinite(item.basePrice) ? item.basePrice : item.price || 0;
            const unitPriceForTotal = Number.isFinite(item.price) ? item.price : baseUnitPrice;
            const lineTotal = Math.max(0, unitPriceForTotal * (item.quantity || 0) - promoDiscount);
            return (
              <div
                key={itemKey}
                className={`flex flex-col sm:flex-row items-start justify-between gap-3 border rounded-xl px-3 py-2 ${
                  item.requiresPrice ? 'border-amber-400 bg-amber-50' : 'border-transparent'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-semibold text-sm text-slate-900 whitespace-normal break-words">{item.name}</p>
                  </div>
                  {(item.miscDescription || item.miscBarcode) && (
                    <p className="text-xs text-slate-500">
                      {item.miscDescription ? `Desc: ${item.miscDescription}` : ''}
                      {item.miscDescription && item.miscBarcode ? ' • ' : ''}
                      {item.miscBarcode ? `Barcode: ${item.miscBarcode}` : ''}
                    </p>
                  )}
                  {item.requiresPrice ? (
                    <p className="text-sm text-amber-700 font-semibold">Price required</p>
                  ) : (
                    <p className="text-sm text-slate-500">
                      {item.quantity} × ${unitPriceForTotal.toFixed(2)} • ${lineTotal.toFixed(2)}
                      {item.overridePrice != null && item.overridePrice !== baseUnitPrice
                        ? ` (Override, base ${baseUnitPrice.toFixed(2)})`
                        : ''}
                    </p>
                  )}
                  {manualDiscount > 0 && (
                    <p className="text-sm text-amber-600 font-semibold">Discount: -${manualDiscount.toFixed(2)}</p>
                  )}
                  {promoDiscount > 0 && (
                    <p className="text-sm text-accent-green font-semibold">Promo: -${promoDiscount.toFixed(2)}</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setActiveAdjustItem(item)}
                    className="min-h-11 px-2 py-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-1"
                  >
                    <FiTag className="text-slate-500" />
                    Adjust
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveQuantityItem(item)}
                    className="min-h-11 px-3 py-2 rounded-xl border border-slate-200 bg-white text-lg font-semibold text-slate-900 flex items-center gap-1 shadow-inner"
                  >
                    <FiHash className="text-slate-500" />
                    {item.quantity}
                  </button>
                  <button onClick={() => onRemoveItem(itemKey)} className="min-h-11 min-w-11 flex items-center justify-center text-red-500 hover:text-red-700 text-2xl" aria-label={`Remove ${item.name}`}>
                    <FiXCircle />
                  </button>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
      {activeQuantityItem && (
        <QuantityModal
          item={activeQuantityItem}
          onClose={() => setActiveQuantityItem(null)}
          onConfirm={(itemKey, qty) => {
            setActiveQuantityItem(null);
            onUpdateQuantity(itemKey, qty);
          }}
        />
      )}
      {activeAdjustItem && (
        <LineItemAdjustModal
          item={activeAdjustItem}
          discountReasons={discountReasons}
          canApplyDiscount={canApplyDiscount}
          onClose={() => setActiveAdjustItem(null)}
          onApply={(payload) => {
            const itemKey = activeAdjustItem.lineId || activeAdjustItem.productId;
            onAdjustItem(itemKey, payload);
            setActiveAdjustItem(null);
          }}
        />
      )}
    </div>
  );
};

export default Cart;
